import { useChatAuthorization } from "@faire/help-center-self-serve/chat/hooks/useChatAuthorization";
import { EmailUs } from "@faire/help-center-self-serve/components/EmailUs";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { getSettingHelpCenterPhoneCallbackQueueModal } from "@faire/help-center-utils/settings/getSettingHelpCenterPhoneCallbackQueueModal";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import getCallbackQueue from "@faire/web-api--source/api/help-center/phone/callback_queue/get";
import { trackHelpCenterPhoneClick } from "@faire/web-api--source/events/helpCenter/click/phone";
import { trackHelpCenterPhoneOverChatClick } from "@faire/web-api--source/events/helpCenter/click/phoneOverChat";
import { path as getSelfServeRecommendedArticleRoute } from "@faire/web-api--source/routes/support/self-serve/recommended-article";
import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useIssueType } from "../../hooks/useIssueType";
import { useSelectedNodeAnswer } from "../../hooks/useSelectedNodeAnswer";
import { isPhoneInBusinessHours } from "../Phone/isPhoneInBusinessHours";
import { usePhoneAuthorization } from "../Phone/usePhoneAuthorization";
import { selfServePageToStepAndRoute, StepPage } from "../step";

import { ChatButton } from "./ChatButton";
import { trackEmailUsClickWhenChatInBusinessHours } from "./events/trackEmailUsClickWhenChatInBusinessHours";
import { trackEmailUsClickWhenPhoneInBusinessHours } from "./events/trackEmailUsClickWhenPhoneInBusinessHours";
import { LiveChatUnavailable } from "./LiveChatUnavailable";
import { PhoneButton } from "./PhoneButton";

interface IContactOptionsProps {
  loadTimingRef?: React.RefCallback<HTMLElement>;
  hasDescription?: boolean;
}

export const ContactOptions: React.FC<IContactOptionsProps> = ({
  loadTimingRef,
  hasDescription,
}) => {
  const retailerToken = getRetailerToken() ?? "";
  const brandToken = getBrandToken() ?? "";
  const { issueType } = useIssueType();
  const history = useHistory();
  const { hasInstantAnswer, hasHelpArticles } = useSelectedNodeAnswer();

  const { hasChatAccess, isChatInBusinessHours, isChatInHolidayMode } =
    useChatAuthorization();
  const { hasPhoneAccess } = usePhoneAuthorization();

  const trackClick = () => {
    trackEmailUsClickWhenChatInBusinessHours(
      issueType,
      !isChatInBusinessHours || isChatInHolidayMode
    );
    trackEmailUsClickWhenPhoneInBusinessHours(issueType, hasPhoneAccess);
  };

  const callRequestOnClick = async () => {
    if (isChatInBusinessHours && !isChatInHolidayMode) {
      trackHelpCenterPhoneOverChatClick(retailerToken, brandToken, issueType);
    }
    trackHelpCenterPhoneClick(retailerToken, brandToken);

    const isCallbackQueueEnabled =
      getSettingHelpCenterPhoneCallbackQueueModal();
    const phoneFormRoute =
      selfServePageToStepAndRoute[StepPage.PHONE_FORM].route;

    if (!isCallbackQueueEnabled) {
      history.push(phoneFormRoute);
      return;
    }

    const response = await getCallbackQueue();
    const route = response.is_queued
      ? getSelfServeRecommendedArticleRoute({ call_request_modal: "1" })
      : phoneFormRoute;

    history.push(route);
  };

  const renderPhoneChatEmail = () => {
    return (
      <>
        {hasPhoneAccess ? (
          <>
            <PhoneButton onClick={callRequestOnClick} />
            <Spacer width="2x" />
          </>
        ) : null}
        <ChatButton />
        <LiveChatUnavailable />
        {hasChatAccess ? <Spacer width="2x" /> : null}
        <EmailUs
          to={selfServePageToStepAndRoute[StepPage.EMAIL_FORM].route}
          onClick={trackClick}
        />
      </>
    );
  };

  const renderPhoneEmailChat = () => {
    return (
      <>
        {hasPhoneAccess ? (
          <>
            <PhoneButton onClick={callRequestOnClick} />
            <Spacer width="2x" />
          </>
        ) : null}
        <EmailUs
          to={selfServePageToStepAndRoute[StepPage.EMAIL_FORM].route}
          onClick={trackClick}
        />
        <Spacer width="2x" />
        <ChatButton />
        <LiveChatUnavailable />
      </>
    );
  };

  const renderEmailChatPhone = () => {
    return (
      <>
        <EmailUs
          to={selfServePageToStepAndRoute[StepPage.EMAIL_FORM].route}
          onClick={trackClick}
        />
        <Spacer width="2x" />
        <ChatButton />
        <LiveChatUnavailable />
        {hasPhoneAccess ? (
          <>
            {hasChatAccess ? <Spacer width="2x" /> : null}
            <PhoneButton onClick={callRequestOnClick} />
          </>
        ) : null}
      </>
    );
  };

  const renderChatEmailPhone = () => {
    return (
      <>
        <ChatButton />
        <LiveChatUnavailable />
        {hasChatAccess ? <Spacer width="2x" /> : null}
        <EmailUs
          to={selfServePageToStepAndRoute[StepPage.EMAIL_FORM].route}
          onClick={trackClick}
        />
        {hasPhoneAccess ? (
          <>
            <Spacer width="2x" />
            <PhoneButton onClick={callRequestOnClick} />
          </>
        ) : null}
      </>
    );
  };

  const renderChatAndPhone = () => {
    if (!isChatInBusinessHours && !isPhoneInBusinessHours()) {
      return renderEmailChatPhone();
    }
    if (!isChatInBusinessHours && isPhoneInBusinessHours()) {
      return renderPhoneEmailChat();
    }
    if (isChatInBusinessHours && !isPhoneInBusinessHours()) {
      return renderChatEmailPhone();
    }
    return renderPhoneChatEmail();
  };

  return (
    <Column>
      <ContactUsTitleAndDescription
        hasDescription={hasDescription}
        fontIncrease={!hasHelpArticles && !hasInstantAnswer}
      />
      <ContactUsWrapper ref={loadTimingRef}>
        {renderChatAndPhone()}
      </ContactUsWrapper>
    </Column>
  );
};

const ContactUsTitleAndDescription: React.FC<{
  hasDescription?: boolean;
  fontIncrease?: boolean;
}> = ({ hasDescription, fontIncrease }) => {
  return (
    <>
      <Typography
        variant={
          fontIncrease ? "sectionHeaderSansMedium" : "subheadingSansMedium"
        }
      >
        <StrictLocalMsg defaultMessage="Contact us" />
      </Typography>
      {hasDescription ? (
        <>
          <Spacer height="1x" />
          <SubtitleWrapper>
            <Typography variant="paragraphSansRegular">
              <StrictLocalMsg defaultMessage="Our support team will be happy to help. We’ll provide them all the info you’ve shared already." />
            </Typography>
          </SubtitleWrapper>
        </>
      ) : null}
      <Spacer height="1x" />
    </>
  );
};

const ContactUsWrapper = styled(Flex).attrs({
  direction: { desktopAndAbove: "row", mobileAndAbove: "column" },
})`
  ${responsiveMediaQueries.tabletAndBelow} {
    width: 100%;
  }
`;

const SubtitleWrapper = styled(Row)`
  margin-bottom: ${getSpacing("2x")};
  width: 100%;
`;
